import {
  getBeneficiariesList,
  getUnpaidBeneficiariesList,
} from "../api";
import { PRODUCT_IDS, DEFAULT_CARD_VALUES } from "../Constants";

/**
 * Card Strategy Pattern
 * Encapsulates card-specific behavior to eliminate conditional logic
 * and follow the Open/Closed principle
 */

const plasticCardStrategy = {
  // Fetch method specific to plastic cards
  fetchBeneficiaries: getUnpaidBeneficiariesList,
  
  // Default recharge value for plastic cards
  getDefaultRecharge: () => 0,
  
  // Plastic cards don't require recharge validation
  validateRecharge: () => true,
  
  // Recharge validation error message (not used for plastic)
  getRechargeErrorMessage: () => "",
  
  // Calculate price based on fixed price per beneficiary
  calculatePrice: (beneficiaries) => 
    beneficiaries.length * DEFAULT_CARD_VALUES[PRODUCT_IDS.CARD_PLASTIC],
  
  // Should the recharge field be shown in UI?
  showRechargeField: false,
  
  // Should the recharge info text be shown?
  showRechargeInfo: false,
  
  // Should recharge be validated in beneficiary selection?
  requiresRechargeForSelection: false,
  
  // Minimum recharge value
  minRechargeValue: 0,
  
  // Should empty fields validation check recharge?
  validateRechargeInEmptyFields: false,

  // CSV template for importing beneficiaries
  csvTemplateUrl: '/themes/sodexo/assets/csv/beneficiaries-plastic-template.csv',
  csvTemplateFilename: 'beneficiaries-plastic-template.csv',
};

const defaultCardStrategy = {
  // Fetch method for regular cards
  fetchBeneficiaries: getBeneficiariesList,
  
  // Default recharge value based on product ID
  getDefaultRecharge: (productId) => DEFAULT_CARD_VALUES[productId] || 30,
  
  // Regular cards require recharge >= 2
  validateRecharge: (value) => {
    const numericValue = parseFloat(value);
    return !isNaN(numericValue) && numericValue >= 2;
  },
  
  // Error message for invalid recharge
  getRechargeErrorMessage: () => "La valeur de recharge doit être supérieure à 2",
  
  // Calculate price based on sum of recharge values
  calculatePrice: (beneficiaries) => 
    beneficiaries.reduce((total, beneficiary) => 
      total + (beneficiary.rechargeValue || 0), 0),
  
  // Show recharge field in UI
  showRechargeField: true,
  
  // Show recharge info text
  showRechargeInfo: true,
  
  // Recharge is required for selection
  requiresRechargeForSelection: true,
  
  // Minimum recharge value
  minRechargeValue: 2,
  
  // Validate recharge in empty fields check
  validateRechargeInEmptyFields: true,

  // CSV template for importing beneficiaries
  csvTemplateUrl: '/themes/sodexo/assets/csv/beneficiaries-template.csv',
  csvTemplateFilename: 'beneficiaries-template.csv',
};

/**
 * Get the appropriate strategy for a given product ID
 * @param {number} productId - The product ID
 * @returns {object} The strategy object for the product
 */
export const getCardStrategy = (productId) => {
  return productId === PRODUCT_IDS.CARD_PLASTIC 
    ? plasticCardStrategy 
    : defaultCardStrategy;
};

/**
 * Check if a product is a plastic card
 * @param {number} productId - The product ID
 * @returns {boolean}
 */
export const isPlasticCard = (productId) => {
  return productId === PRODUCT_IDS.CARD_PLASTIC;
};

